import { inject, injectable } from "inversify";
import type { QueryResponseType } from "@whatslly/types/QueryResponse";
import { QUERY_RESPONSE_ERROR_TYPES } from "@whatslly/types/QueryResponse";
import { QUERY_RESPONSE_PERSON_TYPES } from "@whatslly/types/Person";
import type { SelectedContactType } from "@whatslly/types/SelectedContactType";
import type { ContentCommandsTypes } from "@whatslly/commons/contentCommands";
import { ContentCommands } from "@whatslly/commons/contentCommands";
import { WidgetCommands } from "@whatslly/commons/widgetCommands";
import { SERVER_ERROR_TYPES } from "@whatslly/types/ServerErrorTypes";
import { backgroundClient } from "@whatslly/commons/background/client";
import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import type { InstantMessagingService } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { IInstantMessagingServiceSymbol } from "@whatslly/types/instant-messaging/InstantMessagingService";
import type { EventDataType, ListenerService } from "@whatslly/types/instant-messaging/ListenerService";
import { DOM_EVENT_TYPES, IListenerServiceSymbol } from "@whatslly/types/instant-messaging/ListenerService";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import Emittery from "emittery";
import type { WebScriptCommandsTypes } from "@whatslly/commons/webScriptCommands";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { widgetClient } from "@whatslly/commons/widgetClient";
import { ConversationFilterService } from "./conversationFilterService";
import { BlurMessageService } from "./blurMessageService";
import { retry } from "@whatslly/commons/utils";
import { BlockProfileImageService } from "./blockProfileImageService";
import { AUTH_RESPONSE_ERROR_TYPES } from "@whatslly/types/AuthResponse";
import { MessageSyncService } from "./messageSyncService";
import type { Translations } from "./i18nService";
import { IMUserStore } from "@whatslly/instant-messaging/IMUserStore";
import { BgCommands } from "@whatslly/commons/bgCommands";
import type { IBlockMessaging } from "./blockMessaging";
import { InjectBlockMessaging } from "./blockMessaging";
import { IMChannelType, InjectChannelType } from "./channelTypes";
import type { ISendMessage } from "./sendMessage";
import { InjectSendMessage } from "./sendMessage";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { WebScriptBackendSymbol } from "@whatslly/types/webScriptBackendSymbol";
import type { MessagingBackend } from "@whatslly/commons/messagingBackend";
import { ContentBackendSymbol } from "@whatslly/types/contentBackendSymbol";
import type { IDOMInjectorService } from "@whatslly/instant-messaging/commons/IDOMInjectorService";
import { IDOMInjectorServiceSymbol } from "@whatslly/instant-messaging/commons/IDOMInjectorService";
import { MessageFraudDetectorService } from "@whatslly/instant-messaging/messageFraudDetector";
import type { IKeywordInsights } from "@whatslly/instant-messaging/messageKeywordInsights";
import { InjectKeywordInsights } from "@whatslly/instant-messaging/messageKeywordInsights";
import type { ISurveillance } from "@whatslly/instant-messaging/messageSurveillanceService";
import { InjectSurveillance } from "@whatslly/instant-messaging/messageSurveillanceService";
import { ContactService } from "@whatslly/instant-messaging/contactService";
import type { ReportGroupsResponseType } from "@whatslly/types/integrations/schemas/group-metadata/GroupMetadataTypes";
import type { ReportGroupEventsResponseType } from "@whatslly/types/integrations/schemas/group-metadata/GroupEventTypes";
import { VoiceVideoRecordingService } from "./voiceVideoRecordingService";
import { WaNotificationToneService } from "@whatslly/instant-messaging/whatsapp/waNotificationToneService";
import omit from "lodash/omit";

@injectable()
export class ChatService extends Emittery<EventDataType> {
  private selectors: WhatsappSelectorsType | undefined;
  private logger: FrontLogger;
  private abortController: AbortController | undefined;
  private abortControllerCollectAllContacts: AbortController | undefined;

  constructor(
    @inject(FrontLogger) private frontLogger: FrontLogger,
    @inject(IInstantMessagingServiceSymbol) private readonly instantMessagingService: InstantMessagingService,
    @inject(IListenerServiceSymbol) private readonly domListenerService: ListenerService,
    @inject(MessageSyncService) private readonly messageSyncService: MessageSyncService,
    @inject(ConversationFilterService) private readonly conversationFilterService: ConversationFilterService,
    @inject(BlurMessageService) private readonly blurMessageService: BlurMessageService,
    @inject(BlockProfileImageService) private readonly blockProfileImageService: BlockProfileImageService,
    @inject(InjectBlockMessaging) private readonly blockMessagingService: IBlockMessaging,
    @inject(IMUserStore) private readonly imUserStore: IMUserStore,
    @inject(InjectSendMessage) private readonly sendMessage: ISendMessage,
    @inject(InjectChannelType) private readonly channelType: IMChannelType,
    @inject(InjectStorage) private readonly storage: IStorage,
    @inject(WebScriptBackendSymbol) private readonly webScriptBackend: MessagingBackend<WebScriptCommandsTypes>,
    @inject(ContentBackendSymbol) private readonly contentBackend: MessagingBackend<ContentCommandsTypes>,
    @inject(IDOMInjectorServiceSymbol) private readonly domInjectorService: IDOMInjectorService,
    @inject(MessageFraudDetectorService) private readonly messageFraudDetectorService: MessageFraudDetectorService,
    @inject(InjectKeywordInsights) private readonly messageKeywordInsightsService: IKeywordInsights,
    @inject(InjectSurveillance) private readonly messageSurveillanceService: ISurveillance,
    @inject(ContactService) private readonly contactService: ContactService,
    @inject(VoiceVideoRecordingService) private readonly voiceVideoRecordingService: VoiceVideoRecordingService,
    @inject(WaNotificationToneService) private readonly waNotificationToneService: WaNotificationToneService
  ) {
    super();
    this.logger = this.frontLogger.getLogger("chat-service");
  }

  flushMessages() {
    let hasPendingMessages = false;
    if (this.voiceVideoRecordingService.sendQueue.size) {
      this.voiceVideoRecordingService.sendQueue.flush();
      hasPendingMessages = true;
    }
    if (this.messageSyncService.sendQueue.size) {
      this.messageSyncService.sendQueue.flush();
      hasPendingMessages = true;
    }
    return hasPendingMessages;
  }

  getWapVersion() {
    return this.instantMessagingService.getWapVersion();
  }

  async query(
    selectedContact?: SelectedContactType,
    personId?: string,
    forceRefresh?: boolean,
    skipDefault?: boolean,
    skipCache?: boolean
  ): Promise<QueryResponseType | undefined> {
    try {
      if (!selectedContact || !selectedContact.success) {
        return;
      }
      const currentTs = Date.now();
      const userPhone = this.instantMessagingService.getMe()?.phone;

      this.flushMessages();
      const start = performance.now();
      const result = await backgroundClient.send(
        BgCommands.QUERY,
        {
          ...selectedContact,
          ts: currentTs,
          id: personId,
          userPhone,
          forceRefresh,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          skipDefault,
          skipCache,
        },
        this.abortController?.signal
      );
      const total = performance.now() - start;
      if (total > 1000) {
        this.logger.warn({ total, orgId: this.imUserStore.user?.organizationId, uid: this.imUserStore.user?._id }, "Slow query detected");
      }
      return result;
    } catch (error) {
      this.logger.warn({ error }, "Query failed");
      return undefined;
    }
  }

  async getMessages() {
    return this.instantMessagingService
      .getAllMessages?.()
      ?.map((m) => ({
        id: m.id,
        text: m.text,
        notifyName: m.notifyName,
        isSentByMe: m.isSentByMe,
        senderPhone: m.senderPhone,
        recipientPhone: m.recipientPhone,
        t: m.t,
      }))
      .filter((m) => m.text);
  }

  async loadEarlierMessages() {
    return await this.instantMessagingService.loadEarlierMessages?.();
  }

  private async handleChangeChatSelection({
    personId,
    forceRefresh,
    skipDefault,
    skipCache,
  }: {
    personId?: string;
    forceRefresh?: boolean;
    skipDefault?: boolean;
    skipCache?: boolean;
  }): Promise<void> {
    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;
    this.blurMessageService.removeUnBlurChatSelectedClass();
    this.logger.debug({}, "selected contact changed");
    const selectedContact = this.instantMessagingService.getSelectedContact();
    const me = this.instantMessagingService.getMe();

    this.logger.debug({ me }, "get me");
    if (!selectedContact?.success) {
      widgetClient.send(WidgetCommands.CONTACT_DESELECTED);
      return;
    }
    const isDesktopLite =
      this.imUserStore.user?.onlyDesktopLiteVersion &&
      !this.imUserStore.user?.allow_tuvis_contacts &&
      this.imUserStore.user?.actions?.showAiChat !== "show";
    this.blurMessageService.injectElement({ loading: true });
    !isDesktopLite && widgetClient.send(WidgetCommands.SHOW_LOADING);
    this.messageFraudDetectorService.resetState(this.imUserStore.user?.phishing_fraud_detection ? { selectedContact } : undefined);
    const [res, img] = await Promise.all([
      this.query(selectedContact, personId, forceRefresh, skipDefault, skipCache),
      this.instantMessagingService.preloadContactImg?.().catch(() => undefined),
    ]);
    if (abortController.signal.aborted) {
      return;
    }
    selectedContact.img = img;
    if (!res && this.imUserStore.user) {
      widgetClient.send(WidgetCommands.QUERY_FAILED);
    } else if (!res && !this.imUserStore.user) {
      widgetClient.send(WidgetCommands.HIDE_LOADING);
    } else if (res && !res.success) {
      this.logger.warn({ res: omit(res, ["user"]) }, "return result success = false");
      if (res.error === QUERY_RESPONSE_ERROR_TYPES.UPDATE_VERSION) {
        widgetClient.send(WidgetCommands.UPDATE_VERSION);
        return;
      }
      if (res.error === QUERY_RESPONSE_ERROR_TYPES.USER_WITHOUT_PERMISSION) {
        widgetClient.send(WidgetCommands.USER_WITHOUT_PERMISSION);
        return;
      }
      if (res.error === QUERY_RESPONSE_ERROR_TYPES.USER_MISSING_PERMISSION) {
        widgetClient.send(WidgetCommands.USER_MISSING_PERMISSION);
        return;
      }
      if (res.error === QUERY_RESPONSE_ERROR_TYPES.GROUPS_NOT_SUPPORTED) {
        this.blurMessageService.removeElement();
        this.blurMessageService.injectElement({ loading: false, unsupportedGroup: true });
        widgetClient.send(WidgetCommands.GROUPS_NOT_SUPPORTED, { user: res.user });
        return;
      }
      if (res.error === QUERY_RESPONSE_ERROR_TYPES.EXTENSION_NOT_SUPPORTED) {
        widgetClient.send(WidgetCommands.EXTENSION_NOT_SUPPORTED, { user: res.user, selectedContact });
        return;
      }
      if ([SERVER_ERROR_TYPES.FAILED_TO_REFRESH_TOKEN].includes(res.error) || res.errorCode === "INVALID_SESSION_ID") {
        widgetClient.send(WidgetCommands.LOGIN_AGAIN);
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ([AUTH_RESPONSE_ERROR_TYPES.API_CURRENTLY_DISABLED, AUTH_RESPONSE_ERROR_TYPES.APEX_REST_SERVICES_DISABLED].includes(res.error as any)) {
        widgetClient.send(WidgetCommands.QUERY_FAILED, { error: res.error });
        return;
      }
      widgetClient.send(WidgetCommands.QUERY_FAILED, { error: res.error });
      return;
    } else if (res && res.success) {
      const commonData = {
        selectedContact,
        user: res.user,
        msgs: res.msgs,
        fields: res.fields,
        etoken: res.etoken,
      };
      if (res.isGroup || res.group) {
        const hasUnknownParticipants = this.hasUnknownGroupParticipants(selectedContact.groupParticipants, res.group?.members);
        if (hasUnknownParticipants) {
          this.blurMessageService.removeElement();
          this.blurMessageService.injectElement({ loading: false, unknownGroupParticipants: true });
        } else {
          this.blurMessageService.removeElement();
          this.blurMessageService.injectUnBlurChatSelectedClass();
        }

        !isDesktopLite &&
          widgetClient.send(WidgetCommands.SHOW_GROUP_PAGE, {
            ...commonData,
            messagesAreBlocked: hasUnknownParticipants,
            group: res.group,
          });
      } else {
        if (
          !Array.isArray(res.person) &&
          ![QUERY_RESPONSE_PERSON_TYPES.UNKNOWN, QUERY_RESPONSE_PERSON_TYPES.WHATSAPP_CONTACT].includes(res.person.type as any)
        ) {
          this.blurMessageService.removeElement();
          this.blurMessageService.injectUnBlurChatSelectedClass();
        } else {
          this.blurMessageService.removeElement();
          this.blurMessageService.injectElement({ loading: false, isMultipleResults: Array.isArray(res.person) });
        }
        !isDesktopLite &&
          widgetClient.send(WidgetCommands.SHOW_PERSON_PAGE, {
            ...commonData,
            person: res.person,
            matchedBy: res.matchedBy,
            foundMultiple: res.foundMultiple,
            whatsllyPersonId: res.whatslly_person_id,
            opps: res.opps,
            openOpps: res.openOpps,
            foundByDefault: res.foundByDefault,
            originalRecordAmount: res.originalRecordAmount,
            messagesAreBlocked: this.blurMessageService.isEnabled && Array.isArray(res.person),
          });
      }

      this.contactService.contact = {
        contact: selectedContact,
        query: res,
      };

      const messagesSyncPromise = this.messageSyncService.resetState({ query: res, selectedContact });
      widgetClient.send(WidgetCommands.HIDE_LOADING);
      this.domInjectorService.injectStatusBarWidgetAndTemplates(res, this.selectors);
      await messagesSyncPromise;
    }
  }

  private hasUnknownGroupParticipants(
    groupParticipants?: Array<{ name?: string; phone?: string }>,
    groupMembers?: Array<{ type: string; phone: string }>
  ): boolean {
    if (!this.blurMessageService.isGroupBlockingEnabled || !groupParticipants || groupParticipants.length === 0) return false;
    if (!groupMembers || groupMembers.length === 0) return true;
    const memberPhonesSet = new Set(groupMembers.map((m) => m.phone).filter((phone) => phone && phone.length > 0));
    const hasUnknown = groupParticipants.some((participant) =>
      !participant.phone || participant.phone?.length === 0 ? false : !memberPhonesSet.has(participant.phone)
    );
    return hasUnknown;
  }

  private startListenToDomContactChanged(selectors: WhatsappSelectorsType) {
    const mainElement = this.instantMessagingService.hasMainElement(selectors);
    if (mainElement) {
      this.logger.info("main element already exists");
      this.handleChangeChatSelection({}).catch((err) => {
        this.logger.warn({ err }, "failed to handle change chat selection on start listen");
      });
    }
    this.domListenerService.on(DOM_EVENT_TYPES.CONTACT_CHANGED, async () => {
      await this.handleChangeChatSelection({});
    });
  }

  private async checkAndOpenPendingPhone() {
    const pendingPhone = await this.storage.get(STORAGE.PENDING_WHATSAPP_PHONE);

    if (pendingPhone) {
      await this.storage.remove(STORAGE.PENDING_WHATSAPP_PHONE);
      await this.instantMessagingService.openChat?.(pendingPhone, true);
    }
  }

  public sendUserPhoneToWidgetPanel() {
    widgetClient.send(WidgetCommands.SAVE_USER_PHONE, { phone: this.instantMessagingService.getMe()?.phone });
  }

  private startListenToWhatsappHasLoaded() {
    this.domListenerService.on(DOM_EVENT_TYPES.HAS_LOADED, async () => {
      await this.emit(DOM_EVENT_TYPES.HAS_LOADED);
      await this.checkAndOpenPendingPhone();
    });
  }

  private startListenToBadgeCount() {
    setInterval(() => {
      try {
        const counters = this.instantMessagingService.unreadCounters?.();
        if (!counters) return;
        backgroundClient.send(BgCommands.BADGE_COUNT, { tabId: localStorage.getItem("tuvis_view_id") ?? "", count: counters.chats });
      } catch (err) {
        this.logger.error({ err }, "failed to get badge count");
      }
    }, 500);
  }

  private startListenWhenContentScriptReady(selectors: WhatsappSelectorsType) {
    const unsubscribe = this.contentBackend.on(ContentCommands.CONTENT_READY, async () => {
      await this.instantMessagingService.findAndSaveWapJsApi?.();
      unsubscribe();
      this.startListenToDomContactChanged(selectors);
      this.startListenToBadgeCount();
      this.sendUserPhoneToWidgetPanel();
    });
  }

  public getSelectors(): WhatsappSelectorsType | undefined {
    return this.selectors;
  }

  public async syncAllGroups() {
    try {
      const userObj = await this.storage.get(STORAGE.USER);
      if (!userObj?.periodicGroupSync) {
        this.logger.debug("Skipping group sync - periodicGroupSync is disabled");
        return;
      }

      if (this.instantMessagingService.isConnected) {
        await retry(
          () => {
            if (!this.instantMessagingService.isConnected?.()) {
              throw new Error("im is not connected");
            }
          },
          { delayBetweenRetryMs: 1_000, maxRetry: Number.MAX_SAFE_INTEGER }
        ).catch(() => null);
      }

      this.logger.info("Fetching all WhatsApp groups");
      const groupsWithParticipants = await this.instantMessagingService.queryAllGroups?.();
      if (!groupsWithParticipants) {
        this.logger.warn("Failed to query groups");
        return;
      }

      this.logger.info({ groupsWithParticipants: groupsWithParticipants.length }, "Fetched all WhatsApp groups");

      const userPhone = this.instantMessagingService.getMe()?.phone;
      await backgroundClient
        .send<typeof BgCommands.HTTP_REQUEST, ReportGroupsResponseType>(BgCommands.HTTP_REQUEST, {
          urlSuffix: "/integrations/groups-metadata/report",
          method: "POST",
          body: JSON.stringify({
            groups: groupsWithParticipants,
            userPhone,
          }),
          headers: { "Content-Type": "application/json" },
        })
        .catch((err) => {
          this.logger.error({ err }, "Failed to send groups to backend");
        });

      return groupsWithParticipants;
    } catch (err) {
      this.logger.error({ err }, "failed to fetch groups");
    }
  }

  public async syncAllGroupEvents(groupIds?: string[]) {
    try {
      const userObj = await this.storage.get(STORAGE.USER);
      if (!userObj?.periodicGroupSync) {
        this.logger.debug("Skipping group events sync - periodicGroupSync is disabled");
        return;
      }

      const lastSyncTimestamps = (await this.storage.get(STORAGE.GROUP_EVENT_LAST_SYNC)) ?? {};
      this.logger.debug({ groupCount: groupIds?.length }, "Fetching all WhatsApp group events");
      const groupEvents = await this.instantMessagingService.queryAllGroupEvents?.(groupIds, lastSyncTimestamps);
      if (!groupEvents) {
        this.logger.warn("Failed to query group events");
        return;
      }

      this.logger.debug({ count: groupEvents.length }, "Fetched all WhatsApp group events");

      const nowSeconds = Math.floor(Date.now() / 1000);
      const newTimestamps = { ...lastSyncTimestamps };
      for (const groupId of groupIds ?? []) {
        if (!newTimestamps[groupId]) {
          newTimestamps[groupId] = nowSeconds;
        }
      }
      for (const event of groupEvents) {
        if (!newTimestamps[event.groupId] || event.timestamp > newTimestamps[event.groupId]) {
          newTimestamps[event.groupId] = event.timestamp;
        }
      }
      await this.storage.set(STORAGE.GROUP_EVENT_LAST_SYNC, newTimestamps);

      const userPhone = this.instantMessagingService.getMe()?.phone;
      await backgroundClient
        .send<typeof BgCommands.HTTP_REQUEST, ReportGroupEventsResponseType>(BgCommands.HTTP_REQUEST, {
          urlSuffix: "/integrations/groups-metadata/events/report",
          method: "POST",
          body: JSON.stringify({
            groupEvents,
            userPhone,
          }),
          headers: { "Content-Type": "application/json" },
        })
        .catch((err) => {
          this.logger.error({ err }, "Failed to send group events to backend");
        });

      return groupEvents;
    } catch (err) {
      this.logger.error({ err }, "failed to fetch group events");
    }
  }

  public async syncAllContactMessages() {
    try {
      const userObj = await this.storage.get(STORAGE.USER);

      if (!userObj) {
        this.logger.warn("Skipping sync all contact messages, user not found");
        return;
      }

      if (!userObj.periodicAutoSync) {
        return;
      }
      this.logger.debug("Start SYNC_ALL_CONTACT_MESSAGES");
      this.abortControllerCollectAllContacts?.abort();
      this.abortControllerCollectAllContacts = new AbortController();

      this.logger.debug("Start check if instant messaging connected");
      const connected = await retry<boolean>(
        () => {
          if (!this.instantMessagingService.isConnected) {
            this.logger.debug("isConnected is undefined");
            return true;
          }
          const connected = this.instantMessagingService.isConnected();
          this.logger.debug(`isConnected() ${connected}`);
          if (!connected) {
            throw new Error("im is not connected");
          }
          return connected;
        },
        { delayBetweenRetryMs: 1_000, maxRetry: Number.MAX_SAFE_INTEGER, signal: this.abortControllerCollectAllContacts?.signal }
      ).catch((err) => {
        this.logger.warn({ err }, "failed to check is connected for collecting all messages");
        return false;
      });
      if (!connected) {
        return;
      }
      this.logger.debug(`start to get me`);

      const userPhone = this.instantMessagingService.getMe()?.phone;

      const lastPeriodicSync = await this.storage.get(STORAGE.LAST_PERIODIC_SYNC);
      this.logger.debug(`get lastPeriodicSync: ${lastPeriodicSync}`);
      const query = (data: { isGroup: boolean; name: string; phone: string }) =>
        backgroundClient.send(
          BgCommands.QUERY,
          { ...data, searchName: data.name, ts: Date.now(), userPhone, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
          new AbortController().signal
        );
      const maxMessageAge = lastPeriodicSync ? Date.now() - lastPeriodicSync : undefined;
      this.logger.debug({ maxMessageAge }, "Start collecting all contact messages");
      const syncedMessagesCount = await this.messageSyncService.collectAllContactMessages({ user: userObj, maxMessageAge, query });
      if (syncedMessagesCount > 0) {
        await this.storage.set(STORAGE.LAST_PERIODIC_SYNC, Date.now());
      }
      this.logger.debug({ syncedMessagesCount }, "Finished collecting all contact messages");
    } catch (err) {
      this.logger.error({ err }, "failed to collect all messages");
    }
  }

  private registerListeners() {
    this.domInjectorService.startListenToThemeModeChanged();
    this.webScriptBackend
      .on(WebScriptCommands.USER_REFRESHED, (data) => this.imUserStore.setUser(data.user))
      .on(WebScriptCommands.REFRESH_PERSON, (data) => this.handleChangeChatSelection(data))
      .on(WebScriptCommands.GET_ALL_MESSAGES, () => this.getMessages())
      .on(WebScriptCommands.LOAD_EARLIER_MESSAGES, () => this.loadEarlierMessages())
      .on(WebScriptCommands.GET_SELECTED_CONTACT, () => this.instantMessagingService.getSelectedContact())
      .on(WebScriptCommands.GET_ME, () => this.instantMessagingService.getMe())
      .on(WebScriptCommands.INSTANT_MESSAGE_LOGOUT, () => this.instantMessagingService.logout?.())
      .on(WebScriptCommands.GET_CONNECTED_STATUS, async () => {
        const connected = this.instantMessagingService.isConnected?.();
        if (!connected) {
          return { connected: false };
        }
        const userPhone = await retry(
          () => {
            const phone = this.instantMessagingService.getMe()?.phone;

            if (!phone) {
              throw new Error("failed to find phone number");
            }
            return phone;
          },
          { delayBetweenRetryMs: 300, maxRetry: Number.MAX_SAFE_INTEGER }
        );
        return { connected: true, userPhone };
      })
      .on(WebScriptCommands.SYNC_ALL_CONTACT_MESSAGES, async () => {
        this.syncAllContactMessages();
      })
      .on(WebScriptCommands.SYNC_ALL_GROUPS, () => {
        this.syncAllGroups()
          .then((groups) => {
            this.syncAllGroupEvents(groups?.map((g: { id: string }) => g.id));
          })
          .catch((err) => {
            this.logger.error({ err }, "Failed to sync all groups");
          });
      })
      .on(WebScriptCommands.SHOW_FRAUD_DETECTED, (messageId) => {
        this.messageFraudDetectorService.showFraudMessage(messageId);
      });
  }

  async init({ i18n }: { i18n?: Translations }) {
    const selectors = await backgroundClient.send(BgCommands.GET_WHATSAPP_SELECTORS, { channelType: this.channelType });

    if (!selectors) {
      this.logger.error("failed to get wap selectors");
      return;
    }

    this.selectors = selectors;

    this.imUserStore.init(selectors);

    this.instantMessagingService.init?.(selectors);

    this.messageSyncService.init({ selectors, i18n });

    this.messageFraudDetectorService.init({ selectors });
    this.blurMessageService.init(selectors);
    this.blockMessagingService.init(selectors);
    this.waNotificationToneService.init(selectors);
    this.blockProfileImageService.init(selectors);
    this.messageKeywordInsightsService.init(selectors);
    this.messageSurveillanceService.init(selectors);
    this.sendMessage.init?.(selectors);
    this.conversationFilterService.init();
    this.voiceVideoRecordingService.init(selectors);

    this.registerListeners();

    this.domListenerService.startListenToMainChatWindowReplace(selectors);

    this.startListenToWhatsappHasLoaded();
    this.startListenWhenContentScriptReady(selectors);
  }
}
